/**
 * Idempotent normalization / migration for universal_v1 bot_settings (UI + save).
 * Legacy renovation_v1 docs still normalize; soft aliases keep renovation_* names.
 */

import {
  ASBESTOS_POLICY_LEGACY_MAP,
  CANONICAL_APPOINTMENT_TYPE_KEYS,
  CANONICAL_HANDOFF_ROLE_TYPES,
  HANDOFF_TRIGGER_LEGACY_MAP,
  LEGACY_APPOINTMENT_TYPE_KEYS,
  LEGACY_TONE_STYLE_MAP,
  RENOVATION_CALL_APPOINTMENT_TYPE,
  RENOVATION_VISIT_APPOINTMENT_TYPE,
  TENANT_POLICY_LEGACY_MAP,
  UNIVERSAL_BOT_SETTINGS_DEFAULTS,
  UNQUALIFIED_ACTION_LEGACY_MAP,
  cloneRenovationDefaults,
  cloneUniversalDefaults,
} from "./renovationSettingsDefaults";

const DEFAULT_HOLIDAY_PRESETS = [
  { date: "2026-12-25", name: "Christmas Day", surcharge_fee: "$100" },
  { date: "2026-12-26", name: "Boxing Day", surcharge_fee: "$100" },
  { date: "2026-01-01", name: "New Year's Day", surcharge_fee: "$100" },
];

function deepMerge(base, patch) {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    return patch === undefined ? base : patch;
  }
  const out = { ...(base && typeof base === "object" && !Array.isArray(base) ? base : {}) };
  for (const [key, val] of Object.entries(patch)) {
    if (val && typeof val === "object" && !Array.isArray(val) && out[key] && typeof out[key] === "object" && !Array.isArray(out[key])) {
      out[key] = deepMerge(out[key], val);
    } else if (val !== undefined) {
      out[key] = val;
    }
  }
  return out;
}

function servicesListToDict(list, defaults) {
  if (!Array.isArray(list)) return defaults;
  const out = { ...defaults };
  list.forEach((item) => {
    if (!item || !item.title) return;
    if (item.title === "other_services") {
      const raw = item.description || "";
      const sep = raw.indexOf("::");
      out.other_services =
        sep >= 0
          ? { status: raw.slice(0, sep) || "false", description: raw.slice(sep + 2) }
          : { status: raw || "false", description: "" };
    } else {
      out[item.title] = item.description || "false";
    }
  });
  return out;
}

function normalizePublicHolidays(raw, defaults) {
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((h) => ({
      date: h?.date || "",
      name: h?.name || "",
      surcharge_fee: h?.surcharge_fee ?? defaults?.[0]?.surcharge_fee ?? "$100",
    }));
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    if (raw.is_open === false) {
      return [];
    }
    const fee = raw.surcharge_fee || defaults?.[0]?.surcharge_fee || "$100";
    return DEFAULT_HOLIDAY_PRESETS.map((h) => ({ ...h, surcharge_fee: fee }));
  }
  return defaults?.length ? [...defaults] : [...DEFAULT_HOLIDAY_PRESETS];
}

function normalizeAppointmentTypes(raw, defaults) {
  const base = { ...(defaults || {}) };
  const merged = { ...base, ...(raw && typeof raw === "object" ? raw : {}) };
  for (const legacyKey of LEGACY_APPOINTMENT_TYPE_KEYS) {
    if (!(legacyKey in base)) {
      delete merged[legacyKey];
    }
  }
  for (const key of CANONICAL_APPOINTMENT_TYPE_KEYS) {
    if (!merged[key]) {
      merged[key] = base[key] || {
        is_offered: false,
        duration_minutes: 60,
        is_free: true,
        price: null,
        attendee: "",
        prerequisites: [],
      };
    }
  }
  return merged;
}

function normalizeTeamCalendars(raw, defaults) {
  const list = Array.isArray(raw) && raw.length > 0 ? raw : defaults || [];
  return list.map((entry) => {
    const row = { ...(entry || {}) };
    if (!row.label && row.staff_name) {
      row.label = row.staff_name;
    }
    delete row.staff_name;
    if (row.calendar_id && String(row.calendar_id).trim()) {
      row.platform = row.platform || "ghl";
    }
    row.handles_appointment_types = Array.isArray(row.handles_appointment_types)
      ? row.handles_appointment_types
      : [];
    return row;
  });
}

const HANDOFF_ROLE_CONTACT_KEYS = ["role", "name", "phone", "email"];
const HANDOFF_COLLAPSE_TYPE_ORDER = ["general", "emergency", "warranty", "commercial"];

function handoffRoleHasContact(role) {
  if (!role || typeof role !== "object") return false;
  return HANDOFF_ROLE_CONTACT_KEYS.some((key) => String(role[key] || "").trim());
}

function mergeHandoffRoleFields(...roles) {
  const merged = { type: "general" };
  for (const key of HANDOFF_ROLE_CONTACT_KEYS) {
    for (const role of roles) {
      if (!role || typeof role !== "object") continue;
      const val = String(role[key] || "").trim();
      if (val) {
        merged[key] = val;
        break;
      }
    }
  }
  return merged;
}

function resolvePrimaryHandoffRoleFromSettings(handoffSettings) {
  const hs = handoffSettings && typeof handoffSettings === "object" ? handoffSettings : {};
  const rawRoles = hs.roles;
  const rolesList = Array.isArray(rawRoles)
    ? rawRoles.filter((r) => r && typeof r === "object")
    : [];

  let general = null;
  const byType = new Map();
  rolesList.forEach((role) => {
    const roleType = String(role.type || "").toLowerCase();
    if (roleType) byType.set(roleType, role);
    if (roleType === "general") general = role;
  });

  const ordered = [];
  HANDOFF_COLLAPSE_TYPE_ORDER.forEach((roleType) => {
    if (byType.has(roleType)) ordered.push(byType.get(roleType));
  });
  rolesList.forEach((role) => {
    const roleType = String(role.type || "").toLowerCase();
    if (roleType && !HANDOFF_COLLAPSE_TYPE_ORDER.includes(roleType)) {
      ordered.push(role);
    }
  });

  if (ordered.length) {
    const merged = mergeHandoffRoleFields(...ordered);
    if (handoffRoleHasContact(merged)) return merged;
  }

  if (general) return { ...general };

  for (const role of rolesList) {
    if (handoffRoleHasContact(role)) return { ...role };
  }

  const lead = {};
  const legacyMap = [
    ["lead_agent_name", "name"],
    ["lead_agent_role", "role"],
    ["lead_agent_phone", "phone"],
    ["lead_agent_email", "email"],
  ];
  legacyMap.forEach(([src, dst]) => {
    const val = String(hs[src] || "").trim();
    if (val) lead[dst] = val;
  });
  if (Object.keys(lead).length) {
    return { type: "general", ...lead };
  }

  return { type: "general" };
}

function normalizeHandoffRoles(rawRoles, defaultRoles) {
  const defaults = defaultRoles || [];
  const defaultGeneral =
    defaults.find((r) => String(r?.type).toLowerCase() === "general") || defaults[0] || {
      type: "general",
    };
  const incoming = Array.isArray(rawRoles) ? rawRoles : [];
  const byType = new Map();
  incoming.forEach((r) => {
    if (r?.type) {
      byType.set(String(r.type).toLowerCase(), { ...r, type: String(r.type).toLowerCase() });
    }
  });

  const mergedIncoming = HANDOFF_COLLAPSE_TYPE_ORDER.map((key) => byType.get(key)).filter(Boolean);
  byType.forEach((role, key) => {
    if (!HANDOFF_COLLAPSE_TYPE_ORDER.includes(key)) mergedIncoming.push(role);
  });

  const primary = mergeHandoffRoleFields(
    resolvePrimaryHandoffRoleFromSettings({ roles: mergedIncoming }),
    defaultGeneral,
    ...mergedIncoming
  );
  return [{ ...defaultGeneral, ...primary, type: "general" }];
}

/** Reduce handoff_settings.roles[] to single primary role (idempotent). */
export function collapseRenovationHandoffRoles(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (!kb || typeof kb !== "object") return out;
  const kbOut = { ...kb };
  const hs = kbOut.handoff_settings;
  if (!hs || typeof hs !== "object") return out;
  const hsOut = { ...hs };
  const primary = resolvePrimaryHandoffRoleFromSettings(hsOut);
  const collapsed = { type: "general" };
  HANDOFF_ROLE_CONTACT_KEYS.forEach((key) => {
    const val = String(primary[key] || "").trim();
    if (val) collapsed[key] = val;
  });
  hsOut.roles = [collapsed];
  kbOut.handoff_settings = hsOut;
  out.knowledge_base = kbOut;
  return out;
}

function deriveWeeklyFromLegacy(oh) {
  if (!oh || typeof oh !== "object") return null;
  const mtf = oh.monday_to_friday;
  const sat = oh.saturday;
  const sun = oh.sunday;
  const weekly = {};
  for (const day of ["monday", "tuesday", "wednesday", "thursday", "friday"]) {
    if (mtf && typeof mtf === "object") {
      weekly[day] = mtf.is_open === false ? null : { start: mtf.start, end: mtf.end };
    } else if (mtf) {
      weekly[day] = mtf;
    }
  }
  if (sat !== undefined) {
    weekly.saturday =
      sat && typeof sat === "object" && sat.is_open === false
        ? null
        : sat
          ? { start: sat.start, end: sat.end }
          : null;
  }
  if (sun !== undefined) {
    weekly.sunday =
      sun && typeof sun === "object" && sun.is_open === false
        ? null
        : sun
          ? { start: sun.start, end: sun.end }
          : null;
  }
  return Object.keys(weekly).length ? weekly : null;
}

const CHANNEL_SITE_VISIT = "site_visit";
const CHANNEL_PHONE_CONSULT = "phone_consult";
const LEGACY_OPERATING_HOUR_KEYS = ["monday_to_friday", "saturday", "sunday"];
const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function normalizeHoursWindow(raw) {
  if (raw == null || typeof raw !== "object") return null;
  if (raw.is_open === false) return null;
  const start = raw.start?.trim?.() || raw.start || null;
  const end = raw.end?.trim?.() || raw.end || null;
  if (!start && !end) return null;
  return { start, end };
}

function hasWeekdayPerDayKeys(raw) {
  if (!raw || typeof raw !== "object") return false;
  return ["monday", "tuesday", "wednesday", "thursday", "friday"].some((key) =>
    Object.prototype.hasOwnProperty.call(raw, key)
  );
}

function expandLegacyWeeklySchedule(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = { ...raw };
  const mtf = normalizeHoursWindow(out.monday_to_friday);
  if (!hasWeekdayPerDayKeys(out) && mtf) {
    for (const day of ["monday", "tuesday", "wednesday", "thursday"]) {
      if (!(day in out)) out[day] = { ...mtf };
    }
    if (!("friday" in out)) {
      out.friday = { ...mtf };
    }
  }
  delete out.monday_to_friday;
  return out;
}

function normalizeWeeklySchedule(raw) {
  const expanded = expandLegacyWeeklySchedule(raw);
  const out = {};
  for (const key of WEEKDAY_KEYS) {
    out[key] = normalizeHoursWindow(expanded[key]);
  }
  const msg =
    expanded.out_of_hours_message?.trim?.() || expanded.out_of_hours_message || null;
  if (msg) out.out_of_hours_message = msg;
  return out;
}

function scheduleHasHours(sched) {
  if (!sched || typeof sched !== "object") return false;
  if (normalizeHoursWindow(sched.monday_to_friday)) return true;
  return WEEKDAY_KEYS.some((k) => normalizeHoursWindow(sched[k]));
}

function weeklyToChannelSchedule(weekly) {
  if (!weekly || typeof weekly !== "object") return null;
  const out = {};
  for (const day of WEEKDAY_KEYS) {
    const window = normalizeHoursWindow(weekly[day]);
    if (window) out[day] = window;
  }
  return Object.keys(out).length ? out : null;
}

function deriveSiteVisitFromOperatingHours(oh) {
  if (!oh || typeof oh !== "object") return null;
  if (oh.monday_to_friday != null || oh.saturday != null || oh.sunday != null) {
    const out = {};
    const mtf = normalizeHoursWindow(oh.monday_to_friday);
    if (mtf) out.monday_to_friday = mtf;
    if (oh.saturday != null) out.saturday = normalizeHoursWindow(oh.saturday);
    if (oh.sunday != null) out.sunday = normalizeHoursWindow(oh.sunday);
    return Object.keys(out).length ? out : null;
  }
  return weeklyToChannelSchedule(oh.weekly);
}

/** Expand legacy monday_to_friday → per-day keys and strip mtf (idempotent). */
export function normalizeRenovationChannelSchedulesPerDay(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched =
    out.scheduling && typeof out.scheduling === "object" ? { ...out.scheduling } : null;
  if (!sched?.channel_schedules) return out;
  const cs = { ...sched.channel_schedules };
  for (const channel of [CHANNEL_SITE_VISIT, CHANNEL_PHONE_CONSULT]) {
    const normalized = normalizeWeeklySchedule(cs[channel]);
    if (channel === CHANNEL_SITE_VISIT) delete normalized.out_of_hours_message;
    cs[channel] = normalized;
  }
  sched.channel_schedules = cs;
  out.scheduling = sched;
  return out;
}

/** Populate scheduling.channel_schedules from legacy paths (idempotent). */
export function backfillRenovationChannelSchedules(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling && typeof out.scheduling === "object" ? { ...out.scheduling } : {};
  const existing = sched.channel_schedules;
  const cs = existing && typeof existing === "object" ? { ...existing } : {};

  if (!scheduleHasHours(cs[CHANNEL_SITE_VISIT])) {
    const ops = out.knowledge_base?.operations;
    const field = ops?.field_crew_hours;
    if (field && scheduleHasHours(field)) {
      cs[CHANNEL_SITE_VISIT] = normalizeWeeklySchedule(field);
    } else {
      const derived = deriveSiteVisitFromOperatingHours(sched.operating_hours);
      if (derived) cs[CHANNEL_SITE_VISIT] = normalizeWeeklySchedule(derived);
    }
  }

  if (!scheduleHasHours(cs[CHANNEL_PHONE_CONSULT])) {
    const office = out.knowledge_base?.operations?.office_hours;
    if (office && typeof office === "object") {
      cs[CHANNEL_PHONE_CONSULT] = normalizeWeeklySchedule(office);
    }
  }

  if (Object.keys(cs).length) {
    const siteNorm = normalizeWeeklySchedule(cs[CHANNEL_SITE_VISIT]);
    const phoneNorm = normalizeWeeklySchedule(cs[CHANNEL_PHONE_CONSULT]);
    if (!siteNorm.out_of_hours_message) delete siteNorm.out_of_hours_message;
    sched.channel_schedules = {
      [CHANNEL_SITE_VISIT]: siteNorm,
      [CHANNEL_PHONE_CONSULT]: phoneNorm,
    };
  }
  out.scheduling = sched;
  return out;
}

/** Remove legacy hour keys after channel_schedules backfill (idempotent). */
export function stripRenovationLegacyHours(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };

  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      delete opsOut.field_crew_hours;
      delete opsOut.office_hours;
      kbOut.operations = opsOut;
    }
    out.knowledge_base = kbOut;
  }

  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const oh = schedOut.operating_hours;
    if (oh && typeof oh === "object") {
      const ohOut = { ...oh };
      delete ohOut.weekly;
      for (const key of LEGACY_OPERATING_HOUR_KEYS) delete ohOut[key];
      schedOut.operating_hours = ohOut;
    }
    out.scheduling = schedOut;
  }

  return out;
}

function migrateLeadContactToGeneralRole(hs) {
  if (!hs || typeof hs !== "object") return hs;
  const out = { ...hs };
  const roles = Array.isArray(out.roles) ? [...out.roles] : [];
  let generalIdx = roles.findIndex((r) => String(r?.type).toLowerCase() === "general");
  if (generalIdx < 0) return out;
  const general = { ...roles[generalIdx] };
  if (!general.name && out.lead_agent_name) general.name = out.lead_agent_name;
  if (!general.role && out.lead_agent_role) general.role = out.lead_agent_role;
  if (!general.phone && out.lead_agent_phone) general.phone = out.lead_agent_phone;
  if (!general.email && out.lead_agent_email) general.email = out.lead_agent_email;
  if (!general.allowed_channels?.length && out.allowed_channels?.length) {
    general.allowed_channels = out.allowed_channels;
  }
  roles[generalIdx] = general;
  out.roles = roles;
  return out;
}

function coerceMinBudget(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "boolean") return null;
  if (typeof raw === "number") {
    const n = Math.trunc(raw);
    return n > 0 ? n : null;
  }
  const digits = String(raw).replace(/[^\d]/g, "");
  if (!digits) return null;
  const n = parseInt(digits, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeQualificationLogic(ql, defaults) {
  const out = deepMerge(defaults, ql || {});
  if (out.unqualified_action && UNQUALIFIED_ACTION_LEGACY_MAP[out.unqualified_action]) {
    out.unqualified_action = UNQUALIFIED_ACTION_LEGACY_MAP[out.unqualified_action];
  }
  const dr = out.disqualification_rules || {};
  if (dr.tenant_policy && TENANT_POLICY_LEGACY_MAP[dr.tenant_policy]) {
    dr.tenant_policy = TENANT_POLICY_LEGACY_MAP[dr.tenant_policy];
  }
  if (dr.asbestos_policy && ASBESTOS_POLICY_LEGACY_MAP[dr.asbestos_policy]) {
    dr.asbestos_policy = ASBESTOS_POLICY_LEGACY_MAP[dr.asbestos_policy];
  }
  const coerced = coerceMinBudget(dr.min_budget);
  if (coerced == null) delete dr.min_budget;
  else dr.min_budget = coerced;
  out.disqualification_rules = dr;
  return out;
}

function normalizeToneOfVoice(tov, defaults, kb) {
  const out = deepMerge(defaults, tov || {});
  if (out.style && LEGACY_TONE_STYLE_MAP[out.style]) {
    out.style = LEGACY_TONE_STYLE_MAP[out.style];
  }
  if (!out.tagline && kb?.tagline) {
    out.tagline = kb.tagline;
  }
  return out;
}

function normalizeHandoffTriggers(triggers, defaults) {
  const raw = Array.isArray(triggers) && triggers.length ? triggers : defaults || [];
  return raw.map((t) => HANDOFF_TRIGGER_LEGACY_MAP[t] || t);
}

/**
 * Merge DB payload with defaults, then apply canonical shape migrations.
 * @param {object|null|undefined} dbSettings
 * @param {object} [defaults=UNIVERSAL_BOT_SETTINGS_DEFAULTS]
 */
export function normalizeUniversalSettings(
  dbSettings,
  defaults = UNIVERSAL_BOT_SETTINGS_DEFAULTS
) {
  const base = cloneUniversalDefaults();
  const d = defaults || UNIVERSAL_BOT_SETTINGS_DEFAULTS;
  const raw = dbSettings && typeof dbSettings === "object" ? dbSettings : {};

  const kbRaw = raw.knowledge_base || {};
  const kbDef = d.knowledge_base || {};

  let servicesOffered = kbDef.services_offered;
  if (Array.isArray(kbRaw.services_offered)) {
    servicesOffered = servicesListToDict(kbRaw.services_offered, kbDef.services_offered);
  } else if (kbRaw.services_offered) {
    servicesOffered = deepMerge(kbDef.services_offered, kbRaw.services_offered);
  }

  const ohRaw = raw.scheduling?.operating_hours || {};
  const ohDef = d.scheduling?.operating_hours || {};
  const publicHolidays = normalizePublicHolidays(ohRaw.public_holidays, ohDef.public_holidays);
  let weekly = ohRaw.weekly;
  if (!weekly || typeof weekly !== "object") {
    weekly = deriveWeeklyFromLegacy(ohRaw) || ohDef.weekly;
  }

  let handoffSettings = deepMerge(kbDef.handoff_settings, kbRaw.handoff_settings);
  handoffSettings.roles = normalizeHandoffRoles(
    kbRaw.handoff_settings?.roles,
    kbDef.handoff_settings?.roles
  );
  handoffSettings.handoff_triggers = normalizeHandoffTriggers(
    kbRaw.handoff_settings?.handoff_triggers,
    kbDef.handoff_settings?.handoff_triggers
  );
  handoffSettings = migrateLeadContactToGeneralRole(handoffSettings);

  const knowledge_base = deepMerge(kbDef, kbRaw);
  if (!knowledge_base.company_name && knowledge_base.agency_name) {
    knowledge_base.company_name = knowledge_base.agency_name;
  }
  knowledge_base.services_offered = servicesOffered;
  knowledge_base.handoff_settings = handoffSettings;
  if (!knowledge_base.emergency_safety_actions || !Object.keys(knowledge_base.emergency_safety_actions).length) {
    knowledge_base.emergency_safety_actions = { ...(kbDef.emergency_safety_actions || {}) };
  }
  knowledge_base.pricing_policy = deepMerge(kbDef.pricing_policy, kbRaw.pricing_policy);
  knowledge_base.pricing_policy.ballpark_pricing = deepMerge(
    kbDef.pricing_policy?.ballpark_pricing,
    kbRaw.pricing_policy?.ballpark_pricing ||
      (kbRaw.baseline_pricing ? { products: kbRaw.baseline_pricing.products } : {})
  );

  if (!knowledge_base.competitive_strategy?.design_visualisation) {
    knowledge_base.competitive_strategy = {
      ...(knowledge_base.competitive_strategy || {}),
      design_visualisation: {
        ...(kbDef.competitive_strategy?.design_visualisation || {
          offered: false,
          stage_name: "",
          short_description: "",
        }),
      },
    };
  }
  if (
    knowledge_base.pricing_policy?.quote_rules &&
    knowledge_base.pricing_policy.quote_rules.fixed_price_guarantee_lock_description ===
      undefined
  ) {
    knowledge_base.pricing_policy.quote_rules.fixed_price_guarantee_lock_description =
      kbDef.pricing_policy?.quote_rules?.fixed_price_guarantee_lock_description ?? "";
  }

  const scheduling = deepMerge(d.scheduling, raw.scheduling);
  scheduling.operating_hours = {
    ...deepMerge(ohDef, ohRaw),
    public_holidays: publicHolidays,
    weekly,
  };
  scheduling.appointment_types = normalizeAppointmentTypes(
    raw.scheduling?.appointment_types,
    d.scheduling?.appointment_types
  );
  scheduling.team_calendars = normalizeTeamCalendars(
    raw.scheduling?.team_calendars,
    d.scheduling?.team_calendars
  );
  if (!scheduling.booking_channel || typeof scheduling.booking_channel !== "object") {
    scheduling.booking_channel = { ...(d.scheduling?.booking_channel || {}) };
  } else {
    scheduling.booking_channel = {
      visit_type:
        scheduling.booking_channel.visit_type ||
        d.scheduling?.booking_channel?.visit_type ||
        "free_quote_inspection",
      call_type:
        scheduling.booking_channel.call_type ||
        d.scheduling?.booking_channel?.call_type ||
        "phone_video_consult",
    };
  }
  if (!scheduling.media_requirements) {
    scheduling.media_requirements = { ...(d.scheduling?.media_requirements || {}) };
  }

  const qualification_logic = normalizeQualificationLogic(raw.qualification_logic, d.qualification_logic);
  const oos =
    knowledge_base.out_of_scope_services?.length > 0
      ? knowledge_base.out_of_scope_services
      : qualification_logic.disqualification_rules?.out_of_scope_services?.length > 0
        ? qualification_logic.disqualification_rules.out_of_scope_services
        : kbDef.out_of_scope_services;
  knowledge_base.out_of_scope_services = oos;

  const tone_of_voice = normalizeToneOfVoice(raw.tone_of_voice, d.tone_of_voice, knowledge_base);

  return collapseRenovationHandoffRoles(
    stripUnofferedAppointmentTypes(
      normalizeRenovationAppointmentTypes(
        stripRenovationLegacyMinLeadTimeHours(
          backfillRenovationMinLeadTimeByChannel(
            stripRenovationWeekendAutoConfirm(
              stripRenovationProposedSlotCount(
                stripRenovationHeightDisqualification(
                  stripRenovationBasePostcode(
                    stripRenovationFridayBookingAllowed(
                      stripRenovationAllowDoubleBooking(
                        stripRenovationAttendeeAliases(
                          stripRenovationQuotingWorkflow(
                            stripRenovationCalloutFeePolicy(
                              stripRenovationPricingAskTiers(
                                stripRenovationCrmStaleDataYears(
                                  stripRenovationForbiddenClosingPhrases(
                                    stripRenovationLegacyHours(
                                      stripRenovationLunchBreak(
                                        stripRenovationCrewCapacity(
                                          stripRedundantRenovationKeys(
                                            stripDeadRenovationKeys(
                                              normalizeRenovationChannelSchedulesPerDay(
                                                backfillRenovationChannelSchedules({
                                                  bot_type: raw.bot_type || base.bot_type || "universal_v1",
                                                  knowledge_base,
                                                  qualification_logic,
                                                  scheduling,
                                                  tone_of_voice,
                                                })
                                              )
                                            )
                                          )
                                        )
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  );
}

/** Soft-rename alias — callers may keep renovation_* name. */
export const normalizeRenovationSettings = normalizeUniversalSettings;

const ROLE_STRIP_KEYS = new Set([
  "priority",
  "availability_hours",
  "after_hours_action",
  "min_project_size",
]);

/** Remove dead renovation_v1 config keys (idempotent). */
export function stripDeadRenovationKeys(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  delete out.pipeline_policy;

  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    delete kbOut.last_rag_index_update;
    const hs = kbOut.handoff_settings;
    if (hs && typeof hs === "object" && Array.isArray(hs.roles)) {
      kbOut.handoff_settings = {
        ...hs,
        roles: hs.roles.map((r) => {
          if (!r || typeof r !== "object") return r;
          const role = { ...r };
          for (const key of ROLE_STRIP_KEYS) delete role[key];
          return role;
        }),
      };
    }
    out.knowledge_base = kbOut;
  }

  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    delete schedOut.inspection_hours;
    delete schedOut.daylight_rule_enforced;
    delete schedOut.buffer_between_meetings;
    out.scheduling = schedOut;
  }

  return out;
}

const ROLE_REDUNDANT_KEYS = new Set(["allowed_channels"]);
const LEAD_AGENT_KEYS = [
  "lead_agent_name",
  "lead_agent_role",
  "lead_agent_phone",
  "lead_agent_email",
];

/** Remove duplicate renovation_v1 config keys (idempotent). */
export function stripRedundantRenovationKeys(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const toneTagline = out.tone_of_voice?.tagline;

  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    delete kbOut.agency_name;
    if (toneTagline) delete kbOut.tagline;
    delete kbOut.baseline_pricing;
    if (kbOut.service_area && typeof kbOut.service_area === "object") {
      const sa = { ...kbOut.service_area };
      if (!sa.description) delete sa.description;
      if (!sa.suburbs_or_lgas) delete sa.suburbs_or_lgas;
      delete sa.base_postcode;
      kbOut.service_area = sa;
    }
    const hs = kbOut.handoff_settings;
    if (hs && typeof hs === "object") {
      const hsOut = { ...hs };
      for (const key of LEAD_AGENT_KEYS) delete hsOut[key];
      delete hsOut.allowed_channels;
      if (Array.isArray(hsOut.roles)) {
        hsOut.roles = hsOut.roles.map((r) => {
          if (!r || typeof r !== "object") return r;
          const role = { ...r };
          for (const key of ROLE_REDUNDANT_KEYS) delete role[key];
          return role;
        });
      }
      kbOut.handoff_settings = hsOut;
    }
    out.knowledge_base = kbOut;
  }

  const ql = out.qualification_logic;
  if (ql && typeof ql === "object") {
    const qlOut = { ...ql };
    delete qlOut.internal_goal_summary;
    const dr = qlOut.disqualification_rules;
    if (dr && typeof dr === "object") {
      const drOut = { ...dr };
      if (out.knowledge_base?.out_of_scope_services?.length) {
        delete drOut.out_of_scope_services;
      }
      qlOut.disqualification_rules = drOut;
    }
    out.qualification_logic = qlOut;
  }

  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const oh = schedOut.operating_hours;
    if (oh && typeof oh === "object") {
      const ohOut = { ...oh };
      if (ohOut.weekly && typeof ohOut.weekly === "object") {
        for (const key of LEGACY_OPERATING_HOUR_KEYS) delete ohOut[key];
      }
      if (Array.isArray(ohOut.custom_schedule_rules) && ohOut.custom_schedule_rules.length === 0) {
        delete ohOut.custom_schedule_rules;
      }
      schedOut.operating_hours = ohOut;
    }
    out.scheduling = schedOut;
  }

  return out;
}

/** Remove operations.crew_capacity from renovation_v1 bot_settings (idempotent). */
export function stripRenovationCrewCapacity(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      delete opsOut.crew_capacity;
      kbOut.operations = opsOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove operations.quoting_workflow entirely (idempotent). */
export function stripRenovationQuotingWorkflow(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      delete opsOut.quoting_workflow;
      if (Object.keys(opsOut).length > 0) {
        kbOut.operations = opsOut;
      } else {
        delete kbOut.operations;
      }
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Keep only visit + call appointment types; remap booking_channel and calendars. */
export function normalizeRenovationAppointmentTypes(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (!sched || typeof sched !== "object") return out;
  const schedOut = { ...sched };

  const defaults = cloneRenovationDefaults().scheduling?.appointment_types || {};
  const apptRaw = schedOut.appointment_types;
  const apptIn = apptRaw && typeof apptRaw === "object" ? apptRaw : {};

  const visit = { ...(defaults[RENOVATION_VISIT_APPOINTMENT_TYPE] || {}), ...(apptIn[RENOVATION_VISIT_APPOINTMENT_TYPE] || {}) };
  for (const [key, entry] of Object.entries(apptIn)) {
    if (
      CANONICAL_APPOINTMENT_TYPE_KEYS.includes(key) ||
      !entry ||
      typeof entry !== "object"
    ) {
      continue;
    }
    for (const field of ["duration_minutes", "attendee", "label", "is_free", "price", "prerequisites"]) {
      if ((visit[field] === undefined || visit[field] === null || visit[field] === "" || (Array.isArray(visit[field]) && !visit[field].length)) && entry[field] != null && entry[field] !== "") {
        visit[field] = entry[field];
      }
    }
  }
  if (visit.is_offered === undefined) visit.is_offered = true;

  const call = {
    ...(defaults[RENOVATION_CALL_APPOINTMENT_TYPE] || {}),
    ...(apptIn[RENOVATION_CALL_APPOINTMENT_TYPE] || {}),
  };
  call.is_offered = true;
  if (!call.attendee || !String(call.attendee).trim()) {
    call.attendee = "office_staff";
  } else {
    const att = String(call.attendee).trim().toLowerCase();
    if (att !== "office_staff" && att !== "office") {
      call.attendee = "office_staff";
    }
  }

  schedOut.appointment_types = {
    [RENOVATION_VISIT_APPOINTMENT_TYPE]: visit,
    [RENOVATION_CALL_APPOINTMENT_TYPE]: call,
  };

  const bc = schedOut.booking_channel && typeof schedOut.booking_channel === "object"
    ? { ...schedOut.booking_channel }
    : {};
  let visitType = bc.visit_type || RENOVATION_VISIT_APPOINTMENT_TYPE;
  if (
    !CANONICAL_APPOINTMENT_TYPE_KEYS.includes(visitType) ||
    visitType === RENOVATION_CALL_APPOINTMENT_TYPE ||
    LEGACY_APPOINTMENT_TYPE_KEYS.includes(visitType)
  ) {
    visitType = RENOVATION_VISIT_APPOINTMENT_TYPE;
  }
  schedOut.booking_channel = {
    visit_type: visitType,
    call_type: RENOVATION_CALL_APPOINTMENT_TYPE,
  };

  const isOfficeCalendar = (entry) => {
    const label = String(entry?.label || entry?.staff_name || "").toLowerCase();
    if (label.includes("office")) return true;
    const handles = entry?.handles_appointment_types || [];
    return handles.includes(RENOVATION_CALL_APPOINTMENT_TYPE);
  };

  let team = Array.isArray(schedOut.team_calendars) ? [...schedOut.team_calendars] : [];
  team = team.map((entry) => {
    if (!entry || typeof entry !== "object") return entry;
    const row = { ...entry };
    row.handles_appointment_types = [
      isOfficeCalendar(row) ? RENOVATION_CALL_APPOINTMENT_TYPE : RENOVATION_VISIT_APPOINTMENT_TYPE,
    ];
    return row;
  });
  const hasOffice = team.some(
    (c) =>
      c &&
      Array.isArray(c.handles_appointment_types) &&
      c.handles_appointment_types.includes(RENOVATION_CALL_APPOINTMENT_TYPE)
  );
  if (!hasOffice) {
    team.push({
      platform: "ghl",
      calendar_id: "mock-universal-office-001",
      label: "Office",
      handles_appointment_types: [RENOVATION_CALL_APPOINTMENT_TYPE],
    });
  }
  team = ensureVisitCalendarMapped(team);
  schedOut.team_calendars = team;

  out.scheduling = schedOut;
  return out;
}

function popBookingRuleKey(out, key) {
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      const brOut = { ...br };
      delete brOut[key];
      schedOut.booking_rules = brOut;
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbSched = kbOut.scheduling;
    if (kbSched && typeof kbSched === "object") {
      const kbSchedOut = { ...kbSched };
      const br = kbSchedOut.booking_rules;
      if (br && typeof br === "object") {
        const brOut = { ...br };
        delete brOut[key];
        kbSchedOut.booking_rules = brOut;
      }
      kbOut.scheduling = kbSchedOut;
    }
    out.knowledge_base = kbOut;
  }
}

function backfillBookingRulesMinLeadByChannel(br) {
  if (!br || typeof br !== "object") return br;
  const out = { ...br };
  const legacy = out.min_lead_time_hours;
  if (legacy != null && legacy !== "") {
    out.visit_min_lead_time_hours = parseInt(legacy, 10) || 24;
  } else if (out.visit_min_lead_time_hours == null) {
    out.visit_min_lead_time_hours = 24;
  }
  if (out.call_min_lead_time_hours == null) {
    out.call_min_lead_time_hours = 4;
  }
  return out;
}

function backfillBookingRulesMinLeadInDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      schedOut.booking_rules = backfillBookingRulesMinLeadByChannel(br);
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbSched = kbOut.scheduling;
    if (kbSched && typeof kbSched === "object") {
      const kbSchedOut = { ...kbSched };
      const br = kbSchedOut.booking_rules;
      if (br && typeof br === "object") {
        kbSchedOut.booking_rules = backfillBookingRulesMinLeadByChannel(br);
      }
      kbOut.scheduling = kbSchedOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Backfill visit/call min lead time from legacy min_lead_time_hours. */
export function backfillRenovationMinLeadTimeByChannel(doc) {
  return backfillBookingRulesMinLeadInDoc(doc);
}

/** Remove scheduling.booking_rules.min_lead_time_hours (idempotent). */
export function stripRenovationLegacyMinLeadTimeHours(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  popBookingRuleKey(out, "min_lead_time_hours");
  return out;
}

/** Remove scheduling.booking_rules.friday_booking_allowed (idempotent). */
export function stripRenovationFridayBookingAllowed(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  popBookingRuleKey(out, "friday_booking_allowed");
  return out;
}

/** Remove scheduling.booking_rules.proposed_slot_count (idempotent). */
export function stripRenovationProposedSlotCount(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  popBookingRuleKey(out, "proposed_slot_count");
  return out;
}

/** Remove knowledge_base.service_area.base_postcode (idempotent). */
export function stripRenovationBasePostcode(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const sa = kbOut.service_area;
    if (sa && typeof sa === "object") {
      const saOut = { ...sa };
      delete saOut.base_postcode;
      kbOut.service_area = saOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove qualification_logic.disqualification_rules.max_storeys (idempotent). */
export function stripRenovationMaxStoreys(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const ql = out.qualification_logic;
  if (ql && typeof ql === "object") {
    const qlOut = { ...ql };
    const dr = qlOut.disqualification_rules;
    if (dr && typeof dr === "object") {
      const drOut = { ...dr };
      delete drOut.max_storeys;
      qlOut.disqualification_rules = drOut;
    }
    out.qualification_logic = qlOut;
  }
  return out;
}

/** Remove rejection_messages.height_limit (idempotent). */
export function stripRenovationHeightLimitRejection(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const ql = out.qualification_logic;
  if (ql && typeof ql === "object") {
    const qlOut = { ...ql };
    const dr = qlOut.disqualification_rules;
    if (dr && typeof dr === "object") {
      const drOut = { ...dr };
      const rm = drOut.rejection_messages;
      if (rm && typeof rm === "object") {
        const rmOut = { ...rm };
        delete rmOut.height_limit;
        drOut.rejection_messages = rmOut;
      }
      qlOut.disqualification_rules = drOut;
    }
    out.qualification_logic = qlOut;
  }
  return out;
}

/** Remove max_storeys + height_limit rejection copy (idempotent). */
export function stripRenovationHeightDisqualification(doc) {
  return stripRenovationHeightLimitRejection(stripRenovationMaxStoreys(doc));
}

/** Remove scheduling.booking_rules.weekend_auto_confirm (idempotent). */
export function stripRenovationWeekendAutoConfirm(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      const brOut = { ...br };
      delete brOut.weekend_auto_confirm;
      schedOut.booking_rules = brOut;
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbSched = kbOut.scheduling;
    if (kbSched && typeof kbSched === "object") {
      const kbSchedOut = { ...kbSched };
      const br = kbSchedOut.booking_rules;
      if (br && typeof br === "object") {
        const brOut = { ...br };
        delete brOut.weekend_auto_confirm;
        kbSchedOut.booking_rules = brOut;
      }
      kbOut.scheduling = kbSchedOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove scheduling.booking_rules.allow_double_booking (idempotent). */
export function stripRenovationAllowDoubleBooking(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      const brOut = { ...br };
      delete brOut.allow_double_booking;
      schedOut.booking_rules = brOut;
    }
    out.scheduling = schedOut;
  }
  return out;
}

/** Remove scheduling.attendee_aliases (idempotent). Built-in map remains in backend code. */
export function stripRenovationAttendeeAliases(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    delete schedOut.attendee_aliases;
    out.scheduling = schedOut;
  }
  return out;
}

/** @deprecated Use stripRenovationQuotingWorkflow */
export function stripRenovationOnSiteQuoterRole(doc) {
  return stripRenovationQuotingWorkflow(doc);
}

/** Remove quote_rules callout_fee*, waivers; pricing_policy.emergency_callout_fee (idempotent). */
export function stripRenovationCalloutFeePolicy(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };

  const stripPp = (pp) => {
    if (!pp || typeof pp !== "object") return pp;
    const ppOut = { ...pp };
    delete ppOut.emergency_callout_fee;
    const qr = ppOut.quote_rules;
    if (qr && typeof qr === "object") {
      const qrOut = { ...qr };
      delete qrOut.callout_fee;
      delete qrOut.callout_fee_waived_if_proceeds;
      delete qrOut.waivers;
      ppOut.quote_rules = qrOut;
    }
    return ppOut;
  };

  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    if (kbOut.pricing_policy) {
      kbOut.pricing_policy = stripPp(kbOut.pricing_policy);
    }
    out.knowledge_base = kbOut;
  }
  if (out.pricing_policy) {
    out.pricing_policy = stripPp(out.pricing_policy);
  }
  return out;
}

/** Remove knowledge_base.pricing_policy.pricing_ask_tiers (idempotent). */
export function stripRenovationPricingAskTiers(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const pp = kbOut.pricing_policy;
    if (pp && typeof pp === "object") {
      const ppOut = { ...pp };
      delete ppOut.pricing_ask_tiers;
      kbOut.pricing_policy = ppOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove qualification_logic.crm_retriever (stale_data_years only field). Idempotent. */
export function stripRenovationCrmStaleDataYears(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const ql = out.qualification_logic;
  if (ql && typeof ql === "object") {
    const qlOut = { ...ql };
    const cr = qlOut.crm_retriever;
    if (cr && typeof cr === "object") {
      const crOut = { ...cr };
      delete crOut.stale_data_years;
      if (Object.keys(crOut).length > 0) {
        qlOut.crm_retriever = crOut;
      } else {
        delete qlOut.crm_retriever;
      }
    }
    out.qualification_logic = qlOut;
  }
  return out;
}

/** Remove qualification_logic.interviewer_policy.forbidden_closing_phrases (idempotent). */
export function stripRenovationForbiddenClosingPhrases(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const ql = out.qualification_logic;
  if (ql && typeof ql === "object") {
    const qlOut = { ...ql };
    const ip = qlOut.interviewer_policy;
    if (ip && typeof ip === "object") {
      const ipOut = { ...ip };
      delete ipOut.forbidden_closing_phrases;
      qlOut.interviewer_policy = ipOut;
    }
    out.qualification_logic = qlOut;
  }
  return out;
}

/** Remove scheduling.operating_hours lunch keys from renovation_v1 (idempotent). */
export function stripRenovationLunchBreak(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const oh = schedOut.operating_hours;
    if (oh && typeof oh === "object") {
      const ohOut = { ...oh };
      delete ohOut.lunch_break;
      delete ohOut.lunch_breaks;
      schedOut.operating_hours = ohOut;
    }
    out.scheduling = schedOut;
  }
  return out;
}

function ensureVisitCalendarMapped(team) {
  const list = Array.isArray(team) ? [...team] : [];
  const hasVisit = list.some(
    (c) =>
      c &&
      Array.isArray(c.handles_appointment_types) &&
      c.handles_appointment_types.includes(RENOVATION_VISIT_APPOINTMENT_TYPE)
  );
  if (hasVisit) return list;
  const real = list.filter((c) => c && String(c.calendar_id || "").trim());
  if (real.length !== 1) return list;
  const idx = list.indexOf(real[0]);
  if (idx < 0) return list;
  const row = { ...list[idx] };
  const handles = [...(row.handles_appointment_types || [])];
  if (!handles.includes(RENOVATION_VISIT_APPOINTMENT_TYPE)) {
    handles.push(RENOVATION_VISIT_APPOINTMENT_TYPE);
    row.handles_appointment_types = handles;
    list[idx] = row;
  }
  return list;
}

/** Non-blocking warnings for scheduling / team_calendars misconfiguration. */
export function getRenovationSchedulingWarnings(scheduling) {
  const warnings = [];
  if (!scheduling || typeof scheduling !== "object") return warnings;
  const visitType =
    scheduling?.booking_channel?.visit_type || RENOVATION_VISIT_APPOINTMENT_TYPE;
  const team = Array.isArray(scheduling.team_calendars) ? scheduling.team_calendars : [];
  const hasRealCalendar = team.some((e) => String(e?.calendar_id || "").trim());
  const visitMapped = team.some(
    (e) =>
      String(e?.calendar_id || "").trim() &&
      Array.isArray(e?.handles_appointment_types) &&
      e.handles_appointment_types.includes(visitType)
  );
  if (hasRealCalendar && !visitMapped) {
    warnings.push(
      `No GHL calendar maps "${visitType}". Add it under Team calendars → Handles appointment types so site visits can book automatically.`
    );
  }
  return warnings;
}

function appointmentTypeKeepKeys(scheduling) {
  const keep = new Set();
  const bc = scheduling?.booking_channel;
  if (bc && typeof bc === "object") {
    if (bc.visit_type) keep.add(bc.visit_type);
    if (bc.call_type) keep.add(bc.call_type);
  }
  for (const entry of scheduling?.team_calendars || []) {
    for (const t of entry?.handles_appointment_types || []) {
      if (t) keep.add(t);
    }
  }
  return keep;
}

/** Drop unoffered appointment types except booking/calendar whitelisted keys. */
export function stripUnofferedAppointmentTypes(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (!sched || typeof sched !== "object") return out;
  const schedOut = { ...sched };
  const appt = schedOut.appointment_types;
  if (!appt || typeof appt !== "object") return out;
  const keep = appointmentTypeKeepKeys(schedOut);
  const filtered = {};
  for (const [key, entry] of Object.entries(appt)) {
    if (!entry || typeof entry !== "object") continue;
    if (entry.is_offered !== false || keep.has(key)) filtered[key] = entry;
  }
  schedOut.appointment_types = filtered;
  out.scheduling = schedOut;
  return out;
}

export { CANONICAL_HANDOFF_ROLE_TYPES, DEFAULT_HOLIDAY_PRESETS };
